import { AxiosRequestConfig } from 'axios';
import { axiosClient } from '@as-react/common-utils';
import {
  AemConfiguration,
  PostalMunicipality,
  PostalStreet,
  PostalHouseNumber,
  PostalBoxNumber,
} from '@as-react/common-types';
import { QueryApiOptions, UrlMapping } from '../../types/types';
import { postalQueryKeys } from './postal.queries';
import { QueryFunctionContext } from '@tanstack/react-query';
import { createUrl } from '../../utils/createUrl';

export type PostalMunicipalityParams = {
  query: string;
  lang: AemConfiguration['lang'];
  city?: string;
};

export type PostalStreetParams = PostalMunicipalityParams & {
  municipality: string;
  postcode: string;
};

export type PostalHouseNumberParams = PostalStreetParams & {
  street: string;
};

export type PostalBoxNumberParams = PostalHouseNumberParams & {
  houseNumber: string;
};

const urlMapping: UrlMapping<typeof postalQueryKeys> = {
  municipality: {
    client: 'api/aem/general/autocomplete/be/municipality',
  },
  street: {
    client: 'api/aem/general/autocomplete/be/street',
  },
  houseNumber: {
    client: 'api/aem/general/autocomplete/be/housenumber',
  },
  boxNumber: {
    client: 'api/aem/general/autocomplete/be/boxnumber',
  },
};

export const getMunicipality = async (
  { servicesEndpoint, isServer }: QueryApiOptions,
  { query, lang }: PostalMunicipalityParams,
  { signal }: QueryFunctionContext
): Promise<Array<PostalMunicipality>> => {
  const url = createUrl(servicesEndpoint, urlMapping.municipality, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: {
      query,
      language: lang,
    },
    signal,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const getStreet = async (
  { servicesEndpoint, isServer }: QueryApiOptions,
  { municipality, postcode, query, lang }: PostalStreetParams,
  { signal }: QueryFunctionContext
): Promise<Array<PostalStreet>> => {
  const url = createUrl(servicesEndpoint, urlMapping.street, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: {
      query,
      language: lang,
      municipality,
      postcode,
    },
    signal,
    withCredentials: true,
  };
  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const getHouseNumber = async (
  { servicesEndpoint, isServer }: QueryApiOptions,
  { municipality, postcode, street, query, lang }: PostalHouseNumberParams,
  { signal }: QueryFunctionContext
): Promise<Array<PostalHouseNumber>> => {
  const url = createUrl(servicesEndpoint, urlMapping.houseNumber, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: {
      query,
      language: lang,
      municipality,
      postcode,
      street,
    },
    signal,
    withCredentials: true,
  };
  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const getBoxNumber = async (
  { servicesEndpoint, isServer }: QueryApiOptions,
  { municipality, postcode, street, houseNumber, query, lang }: PostalBoxNumberParams,
  { signal }: QueryFunctionContext
): Promise<Array<PostalBoxNumber>> => {
  const url = createUrl(servicesEndpoint, urlMapping.boxNumber, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: {
      query,
      language: lang,
      municipality,
      postcode,
      street,
      housenumber: houseNumber,
    },
    signal,
    withCredentials: true,
  };
  const result = await axiosClient.request(axiosConfig);
  return result.data;
};
